// ============================================================================
// The classification queue. Run B Phase 3, 2026-08-18.
//
// Jack's second tab. Whatever an AM could not find lands here, and the AM's pay
// and the client's invoice are NEVER held up while it waits — that is
// deliberate, because holding money is what taught everyone to force-fit and
// gave us nine spellings of Strip & Wax.
//
// An action cannot be applied without a written line to the AM.
// ============================================================================
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../../shared/auth';
import { Button, ErrorState, PageHeader } from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';
import { useEmployeeNames } from '../hooks/use-project-data';
import { useServiceCatalog } from '../hooks/use-service-catalog';
import {
  canBounceToAm,
  classifyEvent,
  escalateForNewCode,
  mapLineToService,
  mapServiceLineToCode,
  pushBackToAm,
  splitLine,
  useClassificationQueue,
  type QueueItem,
} from './use-classification-queue';

const INPUT_CLASS =
  'block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

type Action = 'map' | 'split' | 'escalate' | 'pushback' | 'event';

const money = (n: number | null) =>
  n === null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ClassificationQueue() {
  const { employee } = useAuth();
  const { items, isLoading, error, refetch } = useClassificationQueue();
  const catalog = useServiceCatalog();
  const [openId, setOpenId] = useState<string | null>(null);

  const eids = useMemo(() => items.map((i) => i.created_by_eid), [items]);
  const names = useEmployeeNames(eids);

  const open = items.find((i) => i.item_id === openId) ?? null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <PageHeader
        title="Classification queue"
        meta="What an AM could not name. Nobody's pay and no client invoice is held up while it sits here."
      />

      {error && (
        <div className="mb-4">
          <ErrorState title="Couldn't load the queue" body={error} />
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-tcs-text-secondary">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-tcs-text-secondary">Nothing waiting. The vocabulary is holding.</p>
      ) : (
        <>
          <p className="mb-3 text-[12.5px] text-tcs-text-secondary">
            {items.length} waiting ·{' '}
            {items.filter((i) => !canBounceToAm(i.stage)).length} already past Draft, where a
            push-back writes the note but leaves the billing stage alone.
          </p>
          <div className="overflow-hidden rounded-xl border border-tcs-border bg-tcs-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-tcs-field text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                  <th className="px-3 py-2 font-medium">Event</th>
                  <th className="px-3 py-2 font-medium">Facility</th>
                  <th className="px-3 py-2 font-medium">What the AM wrote</th>
                  <th className="px-3 py-2 font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Stage</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.item_id} className="border-t border-tcs-border align-top">
                    <td className="px-3 py-2.5">
                      {/* ⛔ A standing line has no project, so it names itself and
                          links to its building. It used to build both from the
                          project id regardless, which rendered an empty reference
                          and a link to /project-work/null. */}
                      <Link
                        to={
                          i.project_id
                            ? `/project-work/${i.project_id}`
                            : `/account-management/facilities/${i.facility_id}`
                        }
                        className="font-mono text-[12px] text-tcs-text hover:underline"
                      >
                        {i.project_id ?? i.item_id}
                      </Link>
                      <div className="text-[11px] text-tcs-text-secondary">
                        {names[i.created_by_eid ?? ''] ?? i.created_by_eid ?? 'unknown'}
                        {i.when_ ? ` · ${i.when_}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {i.facility_name ?? '—'}
                      <div className="text-[11px] text-tcs-text-secondary">{i.account_name ?? ''}</div>
                    </td>
                    <td className="max-w-[320px] px-3 py-2.5 text-tcs-text-secondary">
                      {i.words ?? <span className="italic">nothing written</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[12px]">
                      {money(i.client_rate ?? i.bill_amount)}
                    </td>
                    <td className="px-3 py-2.5 text-[11.5px] text-tcs-text-secondary">{i.stage ?? '—'}</td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => setOpenId(openId === i.item_id ? null : i.item_id)}
                        className="rounded-md border border-tcs-border px-2.5 py-1 text-[12px] text-tcs-text-secondary hover:border-tcs-inverse/40 hover:text-tcs-text"
                      >
                        {openId === i.item_id ? 'Close' : 'Classify'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {open && (
        <Resolver
          key={open.item_id}
          item={open}
          catalog={catalog}
          actorEid={employee?.employee_id ?? ''}
          onDone={() => {
            setOpenId(null);
            refetch();
          }}
        />
      )}

      <div className="mt-8 rounded-xl border border-tcs-border bg-tcs-surface p-4">
        <h3 className="text-sm font-semibold text-tcs-text">National AM — still open</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-[12.5px] text-tcs-text-secondary">
          <li>Does this route to you, or to the AM's own regional lead first?</li>
          <li>How fast does it need turning around before an AM stops using the escape hatch?</li>
          <li>
            Push-back exists, but on anything past Draft it writes the note and leaves the billing
            stage alone. Say if you want it to hold billing instead.
          </li>
        </ul>
      </div>
    </div>
  );
}

function Resolver({
  item,
  catalog,
  actorEid,
  onDone,
}: {
  item: QueueItem;
  catalog: ReturnType<typeof useServiceCatalog>;
  actorEid: string;
  onDone: () => void;
}) {
  const [action, setAction] = useState<Action | null>(null);
  const [division, setDivision] = useState('');
  const [serviceCode, setServiceCode] = useState('');
  const [division2, setDivision2] = useState('');
  const [serviceCode2, setServiceCode2] = useState('');
  const [secondWords, setSecondWords] = useState('');
  const [eventDivision, setEventDivision] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const isEvent = item.kind === 'event';
  // ⛔ A STANDING SERVICE LINE IS A THIRD SHAPE. It has no project, so the split,
  // the push-back and the event actions below have nothing to act on — every one
  // of them would have been handed a null project id. It gets the two that mean
  // something: give it the right answer, or say the list needs a new one.
  const isServiceLine = item.kind === 'service_line';
  const eventClass =
    catalog.eventClasses.find((c) => c.division_id === eventDivision)?.event_class ?? '';

  async function apply() {
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      if (!actorEid) throw new Error('No acting employee — sign in again.');
      if (action === 'map' && isServiceLine) await mapServiceLineToCode(item, serviceCode, note);
      else if (action === 'map') await mapLineToService(item, serviceCode, note, actorEid);
      else if (action === 'split')
        await splitLine(item, serviceCode, serviceCode2, secondWords, note, actorEid);
      else if (action === 'escalate') await escalateForNewCode(item, note, actorEid);
      else if (action === 'pushback') {
        const r = await pushBackToAm(item, note, actorEid);
        setOk(
          r.stageMoved
            ? 'Sent back to the AM.'
            : 'Note written. The billing stage was left alone — this project is past Draft.',
        );
      } else if (action === 'event')
        await classifyEvent(item, eventClass, reasonCode || null, note, actorEid);
      else throw new Error('Pick what to do with it.');
      if (action !== 'pushback') onDone();
      else setTimeout(onDone, 1600);
    } catch (e) {
      setErr(errorMessage(e, 'That did not apply.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-tcs-text-muted bg-tcs-surface p-4">
      <h3 className="text-sm font-semibold text-tcs-text">{item.facility_name ?? item.project_id}</h3>
      <p className="font-mono text-[11.5px] text-tcs-text-secondary">
        {[item.project_id, item.item_id, item.stage ?? 'no stage'].filter(Boolean).join(' · ')}
      </p>
      {isServiceLine && (
        <p className="mt-1 text-[11.5px] text-tcs-text-secondary">
          A standing line on this building, not a project. Giving it the right name is the only
          thing to do here — no money moves and nothing is sent anywhere.
        </p>
      )}
      <p className="mt-2 rounded-md bg-tcs-field px-3 py-2 text-[12.5px] text-tcs-text-secondary">
        {item.words ?? 'The AM wrote nothing.'}
      </p>

      <div className="mt-3 space-y-2">
        {isServiceLine && (
          <>
            <Option
              on={action === 'map'}
              onClick={() => setAction('map')}
              title="Say what kind of work it is"
              detail="From the standing-work list. The line keeps the words underneath."
            />
            {action === 'map' && (
              <select
                className={INPUT_CLASS}
                value={serviceCode}
                onChange={(e) => setServiceCode(e.target.value)}
              >
                <option value="">Pick the work…</option>
                {catalog.recurringServices.map((s) => (
                  <option key={s.service_code} value={s.service_code}>
                    {s.display_name}
                  </option>
                ))}
              </select>
            )}
            <Option
              on={action === 'escalate'}
              onClick={() => setAction('escalate')}
              title="The list needs a new kind of work"
              detail="Sends to the COO, who decides create-or-map."
            />
          </>
        )}
        {!isEvent && !isServiceLine && (
          <>
            <Option
              on={action === 'map'}
              onClick={() => setAction('map')}
              title="Map it to a service we already have"
              detail="The AM learns the right name and it joins pricing history"
            />
            {action === 'map' && (
              <ServicePicker
                catalog={catalog}
                division={division}
                setDivision={setDivision}
                serviceCode={serviceCode}
                setServiceCode={setServiceCode}
              />
            )}
            <Option
              on={action === 'split'}
              onClick={() => setAction('split')}
              title="Split it into two lines"
              detail="Two things happened — record them separately. The second line carries no rate, so the client sees the agreed total on one line naming both."
            />
            {action === 'split' && (
              <div className="space-y-2 rounded-md bg-tcs-field p-3">
                <p className="text-[11px] uppercase tracking-wide text-tcs-text-muted">First line</p>
                <ServicePicker
                  catalog={catalog}
                  division={division}
                  setDivision={setDivision}
                  serviceCode={serviceCode}
                  setServiceCode={setServiceCode}
                />
                <p className="pt-1 text-[11px] uppercase tracking-wide text-tcs-text-muted">Second line</p>
                <ServicePicker
                  catalog={catalog}
                  division={division2}
                  setDivision={setDivision2}
                  serviceCode={serviceCode2}
                  setServiceCode={setServiceCode2}
                />
                <input
                  type="text"
                  className={INPUT_CLASS}
                  value={secondWords}
                  onChange={(e) => setSecondWords(e.target.value)}
                  placeholder="What the second line covered"
                />
              </div>
            )}
            <Option
              on={action === 'escalate'}
              onClick={() => setAction('escalate')}
              title="This needs a new service code"
              detail="Sends to the COO, who decides create-or-map."
            />
            <Option
              on={action === 'pushback'}
              onClick={() => setAction('pushback')}
              title="Push it back to the AM"
              detail={
                canBounceToAm(item.stage)
                  ? 'Not enough detail to call it. This one is still Draft, so it goes back to them.'
                  : 'Not enough detail to call it. This one is past Draft — the note is written and the billing stage is left alone.'
              }
            />
          </>
        )}
        {isEvent && (
          <>
            <Option
              on={action === 'event'}
              onClick={() => setAction('event')}
              title="Say what kind of event this was"
              detail="It arrived without one, so it reads Unclassified"
            />
            {action === 'event' && (
              <div className="space-y-2 rounded-md bg-tcs-field p-3">
                <select
                  className={INPUT_CLASS}
                  value={eventDivision}
                  onChange={(e) => {
                    setEventDivision(e.target.value);
                    setReasonCode('');
                  }}
                >
                  <option value="">Pick what kind…</option>
                  {catalog.eventClasses.map((c) => (
                    <option key={c.division_id} value={c.division_id}>
                      {c.display_name}
                    </option>
                  ))}
                </select>
                <select
                  className={INPUT_CLASS}
                  value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value)}
                  disabled={!eventDivision}
                >
                  <option value="">Which one…</option>
                  {catalog.reasonsForDivision(eventDivision || null).map((r) => (
                    <option key={r.reason_code} value={r.reason_code}>
                      {r.display_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      <label className="mt-3 block">
        <span className="mb-1 block text-[11px] text-tcs-text-secondary">
          What the AM will see <span className="text-st-danger-fg">*</span>
        </span>
        <textarea
          className={INPUT_CLASS}
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            isServiceLine
              ? 'Named this line Common area clean. The words you wrote are still on the line.'
              : 'Filed as Special clean. Next time use Cleaning > Special clean and put the detail in the scope box.'
          }
        />
      </label>

      <div className="mt-3 flex items-center gap-2">
        <Button type="button" variant="primary" size="md" onClick={apply} disabled={busy}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? 'Applying…' : 'Apply and notify'}
        </Button>
        <button type="button" onClick={onDone} className="text-sm text-tcs-text-secondary hover:text-tcs-text">
          Cancel
        </button>
      </div>
      {err && <p className="mt-2 text-[12.5px] text-st-danger-fg">{err}</p>}
      {ok && <p className="mt-2 text-[12.5px] text-st-ok-fg">{ok}</p>}
    </div>
  );
}

function Option({
  on,
  onClick,
  title,
  detail,
}: {
  on: boolean;
  onClick: () => void;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`block w-full rounded-md border px-3 py-2 text-left ${
        on ? 'border-tcs-inverse bg-tcs-inverse-soft' : 'border-tcs-border hover:border-tcs-inverse/40'
      }`}
    >
      <span className="block text-[13px] font-medium text-tcs-text">{title}</span>
      <span className="mt-0.5 block text-[11.5px] text-tcs-text-secondary">{detail}</span>
    </button>
  );
}

function ServicePicker({
  catalog,
  division,
  setDivision,
  serviceCode,
  setServiceCode,
}: {
  catalog: ReturnType<typeof useServiceCatalog>;
  division: string;
  setDivision: (v: string) => void;
  serviceCode: string;
  setServiceCode: (v: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <select
        className={INPUT_CLASS}
        value={division}
        onChange={(e) => {
          setDivision(e.target.value);
          setServiceCode('');
        }}
      >
        <option value="">Pick a division…</option>
        {catalog.divisions.map((d) => (
          <option key={d.division_id} value={d.division_id}>
            {d.display_name}
          </option>
        ))}
      </select>
      <select
        className={INPUT_CLASS}
        value={serviceCode}
        onChange={(e) => setServiceCode(e.target.value)}
        disabled={!division}
      >
        <option value="">{division ? 'Pick the work…' : 'Pick a division first'}</option>
        {catalog.servicesForDivision(division).map((s) => (
          <option key={s.service_code} value={s.service_code}>
            {s.display_name}
          </option>
        ))}
      </select>
    </div>
  );
}
